import type { CSSProperties, ReactElement } from "react";
import type { LamaTeamHeatmapOutput } from "@playbookai/widget-schemas";
import type { WidgetBodyProps } from "../types.js";
import {
  LAMA_MATURITY_MAX,
  LAMA_TEAM_DIMENSION_NOTE,
  lamaScorePercentLabel,
} from "../lama-report-presentation.js";

/** Map a 1–max maturity score onto semantic theme-managed heat bands. */
function heatStyle(score: number | null, max: number): CSSProperties {
  if (score == null) {
    return { backgroundColor: "hsl(var(--pb-card))" };
  }
  const t = Math.min(1, Math.max(0, (score - 1) / (max - 1)));
  return {
    backgroundColor:
      t < 1 / 3
        ? "var(--pb-heatmap-low)"
        : t < 2 / 3
          ? "var(--pb-heatmap-mid)"
          : "var(--pb-heatmap-high)",
  };
}

/**
 * LAMA Teams Over Time — the teams × waves maturity heatmap, ported from
 * lama-feature's `LamaTeamHeatmap`. A semantic `<table>` (sr-only caption,
 * row/column headers, per-cell scores) so the grid keeps a full non-visual
 * equivalent — colour is decorative and never the only channel.
 *
 * The empty state is the one this widget actually renders today, and it names
 * the missing CAPABILITY rather than implying late data: LAMA has no team
 * dimension on the delivery spine at all (plan dependency PD5), so the dataset
 * is a typed empty by design and no grouping is fabricated to fill it.
 */
export function LamaTeamHeatmapWidget({
  data,
}: WidgetBodyProps<LamaTeamHeatmapOutput>): ReactElement {
  if (data.teams.length === 0) {
    return (
      <p className="text-sm text-pb-text-muted">{LAMA_TEAM_DIMENSION_NOTE}</p>
    );
  }
  const summary = `Maturity by team and wave: ${data.teams.length} team${
    data.teams.length === 1 ? "" : "s"
  } across ${data.waveLabels.length} wave${
    data.waveLabels.length === 1 ? "" : "s"
  }, scored 1 to ${LAMA_MATURITY_MAX}.`;

  return (
    <div
      className="overflow-x-auto overscroll-x-contain focus-ring"
      /* A horizontally scrollable region MUST be keyboard focusable, or a
         keyboard-only user cannot reach the off-screen waves at all (WCAG 2.1.1)
         — and this grid scrolls precisely when a campaign has enough waves to be
         worth reading. The sibling `lamaMaturityProfile` already uses this exact
         pattern; this one shipped as a bare `overflow-x-auto` div.

         The table's own `<caption class="sr-only">` summarises the grid for a
         screen reader, so the region's label names the SCROLL affordance rather
         than repeating that summary. */
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={0}
      role="region"
      aria-label="Scrollable team maturity heatmap"
    >
      {/* eslint-disable-next-line ds/no-hand-built -- kept: a heat-map grid of spaced coloured cells (chart marks); DataTable has no heat-cell layout. */}
      <table className="w-full border-separate border-spacing-1 text-sm">
        <caption className="sr-only">{summary}</caption>
        <thead>
          <tr className="text-xs text-pb-text-muted">
            <th scope="col" className="px-2 py-1 text-start font-medium">
              Team
            </th>
            {data.waveLabels.map((label, index) => (
              <th
                key={`${label}-${index}`}
                scope="col"
                className="px-1 py-1 text-center font-medium"
              >
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.teams.map((team, teamIndex) => (
            <tr key={team}>
              <th
                scope="row"
                className="whitespace-nowrap px-2 py-1 text-start font-medium text-pb-text-secondary"
              >
                {team}
              </th>
              {(data.rows[teamIndex] ?? []).map((score, waveIndex) => (
                <td
                  key={`${team}-${waveIndex}`}
                  className="px-0.5 py-0.5 text-center"
                >
                  <div
                    className="inline-flex h-8 min-w-11 items-center justify-center rounded text-xs font-medium tabular-nums text-pb-text"
                    style={heatStyle(score, LAMA_MATURITY_MAX)}
                    aria-label={`${team}, ${data.waveLabels[waveIndex]}, ${
                      score != null ? lamaScorePercentLabel(score) : "no data"
                    }`}
                  >
                    {lamaScorePercentLabel(score)}
                  </div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
